// PentestCard
// ------------
// Owns the pentest *action* on the WebApplication page — the Pentest
// Now / Stop Pentest button and the rotating "Test in progress" status
// message beneath it. Renders as plain content (no Card chrome, no
// background, no border) because it now lives *inside* a single
// unified Card alongside TestDetails — the unified Card provides the
// white surface and shadow; this component fills the left 1/4 column
// with centered content. A vertical divider in the unified Card
// separates this column from the Security Status data on the right.
//
// Per-app license awareness (quota is per web app, from useTenantEntitlements):
//   * Hovering the card surfaces a subtle "N of M pentests remaining" cue
//     (plus the access-end date for a one-off license) when a test isn't in
//     flight. Just-in-time, not persistent.
//   * Clicking Pentest Now starts the run immediately — no confirmation gate.
//   * When this app's license blocks a new run (no runs left / access window
//     ended / license inactive), the button is disabled and a persistent
//     banner above it states the reason (mirrors the BE start gate).
//   * An app whose license hasn't loaded (or failed to load) shows no cue and
//     stays clickable. That is a rendering choice, not an exemption: every app
//     is licensed server-side and the BE start gate fails closed, so a click in
//     that state comes back with the rejection reason rather than running a
//     free test. Disabling on an unknown would flash the button dead on load.
//   * While a test is running the license UI is suppressed — the rotating
//     status message takes the slot below the button.

import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";

import PentestButton from "@/components/webApplication/PentestButton";
import { LivePhase } from "@/utils/liveTestState";
import { useTenantEntitlements } from "@/utils/useTenantEntitlements";
import {
  blockReasonMessage,
  entitlementDateQualifier,
} from "@/services/tenantEntitlements.service";
import { formatShortDate } from "@/utils/date";

// 30s rotation matches the previous in-card cadence; comfortably above
// the 5s minimum the test plan calls out, so users with prefers-reduced-
// motion can still follow the messages comfortably.
const ROTATION_INTERVAL_MS = 30_000;

const PROVISIONING_MESSAGES = [
  "Starting test environment...",
  "Provisioning compute resources...",
  "Setting up isolated test workers...",
  "Loading test playbooks...",
  "Initializing AI test models...",
  "Establishing secure connection to your application...",
  "Verifying network reachability...",
  "Final pre-flight checks...",
];

const RUNNING_MESSAGES = [
  "Test in progress...",
  "Exploring application surface...",
  "Identifying endpoints...",
  "Probing input handling...",
  "Testing authentication flows...",
  "Examining session management...",
  "Checking access controls...",
  "Inspecting response headers...",
  "Verifying transport security...",
  "Auditing data exposure...",
];

const STOPPING_MESSAGES = [
  "Tearing down test environment...",
  "Saving test findings...",
  "Persisting results...",
  "Decommissioning test agents...",
  "Releasing compute resources...",
  "Cleaning up infrastructure...",
  "Finalizing report data...",
  "Wrapping up...",
];

interface PentestCardProps {
  // The app this card acts on — quota is per app, so the budget cue and the
  // start gate read this app's license, not a tenant-wide total.
  webAppId: string;
  // The app's single live-run signal — see utils/liveTestState.ts. Drives
  // both the button state and which rotating message set (if any) shows.
  livePhase: LivePhase;
  startTestCallback: () => void;
}

export default function PentestCard({
  webAppId,
  livePhase,
  startTestCallback,
}: PentestCardProps) {
  // A run is "in flight" for gating purposes in any non-"none" phase.
  const testRunning = livePhase !== "none";

  const activeMessages =
    livePhase === "provisioning"
      ? PROVISIONING_MESSAGES
      : livePhase === "stopping"
        ? STOPPING_MESSAGES
        : livePhase === "running"
          ? RUNNING_MESSAGES
          : null;

  const [rotationIndex, setRotationIndex] = useState(0);
  useEffect(() => {
    setRotationIndex(0);
    if (!activeMessages) return undefined;
    const interval = setInterval(() => {
      setRotationIndex((prev) => (prev + 1) % activeMessages.length);
    }, ROTATION_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [activeMessages]);

  const activeMessage = activeMessages?.[rotationIndex] ?? "";

  // Per-app license for THIS web app (null = not yet loaded / read failed).
  const { entitlementForWebApp } = useTenantEntitlements();
  const entitlement = entitlementForWebApp(webAppId);

  // Blocked only when this app's license definitively says so (at limit /
  // expired / inactive). A null license — not yet loaded, or the read failed —
  // stays permissive so the button doesn't flash disabled; the BE gate is
  // authoritative and fails closed if our optimistic read is wrong.
  const blockReason = entitlement?.blockReason ?? null;
  const startDisabled = !testRunning && blockReason !== null;

  const handlePentestClick = () => {
    // Both start and stop dispatch immediately — no confirmation gate.
    // The stop path is the safety action and was never gated; the start
    // path is gated only by the license check below.
    if (!testRunning && startDisabled) return;
    startTestCallback();
  };

  const showUsageHoverCue =
    !testRunning && !!entitlement && blockReason === null;

  return (
    // `group` on the outer wrapper lets the hover cue beneath the
    // button reveal only when the cursor is over this card — keeps
    // the visual quiet by default.
    <div className="group h-full flex flex-col items-center p-6">
      {/* Blocked banner — sits above the button when THIS app's license
          blocks a new pentest (no runs left / access window ended /
          license inactive). Persistent and warm enough to notice
          without alarming; the exact reason mirrors the BE gate. */}
      {!testRunning && blockReason && entitlement && (
        <div className="w-full mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
          <p className="font-medium">{blockReasonMessage(blockReason)}</p>
          {blockReason === "expired" && entitlement.accessExpiresAt && (
            <p className="mt-0.5">
              Access ended {formatShortDate(entitlement.accessExpiresAt)}.
            </p>
          )}
          {/* Being told "none left" without a when is the most frustrating
              version of this banner — it's the one state where the user is
              actively blocked. A subscription refills, so say the date. */}
          {blockReason === "at_limit" && entitlement.periodEndsAt && (
            <p className="mt-0.5">
              Resets {formatShortDate(entitlement.periodEndsAt)}.
            </p>
          )}
        </div>
      )}

      <div className="flex-1" aria-hidden="true" />
      <PentestButton
        livePhase={livePhase}
        onClick={handlePentestClick}
        disabledByLimit={startDisabled}
      />

      <div className="flex-1 w-full flex items-start justify-center pt-3">
        {/* Rotating provisioning / running / stopping message — shown
            whenever a test is in flight. role="status" + aria-live=
            polite announces each new message to screen readers;
            aria-atomic ensures the full phrase is read each time
            rather than just the diff. */}
        {activeMessages && (
          <div
            role="status"
            aria-live="polite"
            aria-atomic="true"
            className="flex items-start gap-1.5 text-xs font-medium text-blue-600 text-center leading-snug px-4"
          >
            <Loader2
              className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none shrink-0 mt-0.5"
              aria-hidden="true"
            />
            <span>{activeMessage}</span>
          </div>
        )}

        {/* Hover-only usage cue. opacity-0 → group-hover:opacity-100
            on the outer card, so the cue stays out of the way until
            the user shows intent. Suppressed when the rotating
            status message is taking the slot. */}
        {!activeMessages && showUsageHoverCue && entitlement && (
          <p className="text-[11px] text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity text-center">
            {entitlement.testsRemaining} of {entitlement.testsAllowed} pentest
            {entitlement.testsAllowed === 1 ? "" : "s"} remaining
            {entitlementDateQualifier(entitlement, formatShortDate)}
          </p>
        )}
      </div>
    </div>
  );
}
